import React from 'react';
import { IconArrowUp, IconArrowDown, IconTrendFlat } from '../../icons';
import { Badge } from '../common/Badge';

/**
 * `cockpit/GrowthBadge` (E3/E5, M-CFO-3, `architecture-cockpit-cfo.md` §8.5).
 *
 * Kleiner, rein präsentationaler Wachstums-/YoY-Indikator zum Anhängen an einen
 * Kachel-/Chart-Header (z. B. Umsatz-Chart-Header §8.5, oder die YoY-Zeile der
 * `ResultYtdTile` §8.3). Zeigt ein bereits lokalisiert zusammengesetztes Label
 * (z. B. „Jun: +18 % ggü. Vorjahr") mit Richtungs-Icon (▲/▼/–) und Ton-Farbe.
 *
 * A11y (§12, WCAG 1.4.1): NIE nur farbkodiert — die Richtung wird zusätzlich
 * per Icon UND ausgeschriebenem `aria-label` transportiert. Der Prozentwert
 * steckt bereits im übergebenen `label` (der Aufrufer formatiert ihn locale-
 * korrekt); diese Komponente entscheidet nur über Ton/Icon anhand `pct`.
 *
 * Fail-closed (§8.5): `pct === null` ⇒ die Komponente rendert `null` (KEIN
 * „— %"). Der Aufrufer muss den Badge dann gar nicht erst zeigen; das
 * Null-Rendering ist die zweite Sicherung.
 *
 * @module components/cockpit/GrowthBadge
 */

export interface GrowthBadgeProps {
  /** Prozentwert (gerundet). `null` ⇒ Badge rendert nicht (kein „— %"). */
  pct: number | null;
  /** Vollständig zusammengesetztes, bereits lokalisiertes Label (z. B. „Jun: +18 % ggü. Vorjahr"). */
  label: string;
  /**
   * Ausgeschriebenes `aria-label` (WCAG 1.4.1) — trägt die Richtung in Worten,
   * falls das sichtbare `label` sie nur per Vorzeichen/Icon zeigt. Default = `label`.
   */
  ariaLabel?: string;
  className?: string;
}

/** Ton + Richtungs-Icon anhand des Vorzeichens (0 ⇒ neutral/flat). */
function toneAndIcon(pct: number): { tone: 'success' | 'danger' | 'neutral'; icon: React.ReactNode } {
  if (pct > 0) return { tone: 'success', icon: <IconArrowUp aria-hidden={true} /> };
  if (pct < 0) return { tone: 'danger', icon: <IconArrowDown aria-hidden={true} /> };
  return { tone: 'neutral', icon: <IconTrendFlat aria-hidden={true} /> };
}

/**
 * Rendert den Wachstums-Badge oder `null` (bei `pct === null`).
 */
const GrowthBadge: React.FC<GrowthBadgeProps> = ({ pct, label, ariaLabel, className }) => {
  if (pct === null) return null;

  const { tone, icon } = toneAndIcon(pct);

  return (
    <Badge tone={tone} className={className}>
      <span role="img" aria-label={ariaLabel ?? label} className="inline-flex items-center gap-1">
        <span aria-hidden="true" className="inline-flex items-center">
          {icon}
        </span>
        <span aria-hidden="true">{label}</span>
      </span>
    </Badge>
  );
};

export default GrowthBadge;
